import * as z from 'zod';

/**
 * Every event sent to n8n (and between services) uses this envelope (plan §3 rule 5):
 * fields are only ever added; a breaking change ships as a new `version`.
 */
export const eventEnvelopeSchema = <T extends z.ZodType>(type: string, version: number, data: T) =>
  z.object({
    id: z.uuid(),
    type: z.literal(type),
    version: z.literal(version),
    occurredAt: z.iso.datetime().meta({ description: 'UTC timestamp (ISO 8601)' }),
    data,
  });

export interface EventDefinition<T extends z.ZodType = z.ZodType> {
  readonly type: string;
  readonly version: number;
  readonly data: T;
  readonly description: string;
  readonly envelope: ReturnType<typeof eventEnvelopeSchema<T>>;
}

const TYPE_PATTERN = /^[a-z]+(_[a-z]+)*(\.[a-z]+(_[a-z]+)*)+$/;

/** Declares an event type. `type` is dotted snake_case, e.g. `order.status_changed`. */
export function defineEvent<T extends z.ZodType>(def: {
  type: string;
  version: number;
  data: T;
  description: string;
}): EventDefinition<T> {
  if (!TYPE_PATTERN.test(def.type)) {
    throw new Error(`event type must be dotted snake_case, got "${def.type}"`);
  }
  if (!Number.isInteger(def.version) || def.version < 1) {
    throw new Error(`event version must be a positive integer, got ${def.version}`);
  }
  return { ...def, envelope: eventEnvelopeSchema(def.type, def.version, def.data) };
}

export type EventEnvelope<T = unknown> = {
  id: string;
  type: string;
  version: number;
  occurredAt: string;
  data: T;
};
